import React, { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { PlatformCustomRole } from "@/types/platform-role";

interface PlatformRolePickerProps {
  roles: PlatformCustomRole[];
  selectedRoleId: string | null;
  isLoading: boolean;
  label: string;
  description: string;
  onSelectedRoleIdChange: (roleId: string | null) => void;
  disabled?: boolean;
}

export const PlatformRolePicker: React.FC<PlatformRolePickerProps> = ({
  roles,
  selectedRoleId,
  isLoading,
  label,
  description,
  onSelectedRoleIdChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputId = useId();
  const [searchValue, setSearchValue] = useState("");

  const filteredRoles = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) {
      return roles;
    }

    return roles.filter((role) => role.name.toLowerCase().includes(search));
  }, [roles, searchValue]);

  const toggleRole = (roleId: string) => {
    onSelectedRoleIdChange(selectedRoleId === roleId ? null : roleId);
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        id={inputId}
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        icon={<Search className="text-muted-foreground h-4 w-4" />}
        placeholder={t("users.groups.create.searchPlaceholder", "Search")}
        label={label}
        description={description}
        disabled={disabled}
      />
      <div className="border-border max-h-[220px] overflow-y-auto rounded-md border p-2">
        {isLoading ? (
          <p className="text-muted-foreground px-2 py-1 text-sm">
            {t("common.loading", "Loading...")}
          </p>
        ) : null}
        {!isLoading && filteredRoles.length === 0 ? (
          <p className="text-muted-foreground px-2 py-1 text-sm">
            {t("users.groups.create.noRolesFound", "No roles found")}
          </p>
        ) : null}
        {!isLoading
          ? filteredRoles.map((role) => {
              const checkboxId = `${inputId}-role-${role.id}`;

              return (
                <div
                  key={role.id}
                  className="hover:bg-muted flex items-center gap-2 rounded-sm px-2 py-1.5"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={selectedRoleId === role.id}
                    onCheckedChange={() => toggleRole(role.id)}
                    disabled={disabled}
                  />
                  <label
                    htmlFor={checkboxId}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                  >
                    <span className="text-foreground truncate text-sm">
                      {role.name}
                    </span>
                    <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                      {t("users.groups.table.usersCount", "{{count}} users", {
                        count: role.assigneeCount,
                      })}
                    </span>
                  </label>
                </div>
              );
            })
          : null}
      </div>
    </div>
  );
};
